/**
 * The top-nav overdue-invoice alert: a red dot on the **CRM** tab, and — every
 * few seconds — a red wave that sweeps across the pill and breaks on the dot.
 *
 * Why it exists: the CRM's warning bar only shows up once you are already
 * *inside* the CRM. Money nobody chases is money nobody collects, so the debt
 * has to be visible from wherever you happen to be working — the Dashboard,
 * Chat, Documentation. The nav is the one surface that is always on screen.
 *
 * Why it stays polite:
 *  - **It only appears for someone who can act on it.** The feed
 *    (`lib/crm/overdueBadge.ts`) is gated on `crm:view` + `crm:financials` and
 *    scoped per-actor by crm-api, so a salesperson whose own invoices are all
 *    current sees nothing, and a role that may not see money never even asks.
 *  - **The dot is the constant; the wave is the reminder.** Between waves the
 *    tab is an ordinary nav pill with a small red dot. The wave runs on a slow
 *    cycle (12s at `warning`, 6s at `critical`) rather than a permanent
 *    animation that turns into visual noise you stop seeing.
 *  - **Reduced motion kills all of it.** Under
 *    `prefers-reduced-motion: reduce` the wave and the ripple are off and the
 *    dot is simply there — the information survives, the movement doesn't.
 *  - **Screen readers get the sentence, not the decoration.** The dot is
 *    `aria-hidden`; the tab carries a visually-hidden `role="status"` line
 *    ("6 invoices are overdue · …"), which is also the hover tooltip.
 */
import { Box, Tooltip } from "@mui/material";
import { alpha, type SxProps, type Theme } from "@mui/material/styles";
import {
  overdueCountPhrase,
  overdueOldestPhrase,
  type OverdueSeverity,
  type OverdueTotals,
} from "@xop/dashboard-shared";

/** How long one wave cycle takes. Critical debt nags twice as often. */
export function waveCycleMs(severity: OverdueSeverity): number {
  return severity === "critical" ? 6000 : 12000;
}

/** Whole-dollar USD ("$5,212") — an operator chasing a payment wants the real
 * figure, and the cents are noise in a tooltip. Matches the CRM banner, which
 * renders its total at `decimals: 0`. */
export function formatOutstanding(amount: number): string {
  if (!Number.isFinite(amount) || amount <= 0) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * The one sentence the alert says — tooltip, `aria-label`, and the live region.
 * Pure, so the copy is unit-tested without rendering anything.
 *
 * The amount is money: this only ever runs for a holder of `crm:financials`
 * (the feed refuses to fetch otherwise), and it is dropped entirely when the
 * total doesn't parse, rather than rendering a misleading "$0".
 */
export function overdueNavTooltip(totals: OverdueTotals): string {
  const parts = [overdueCountPhrase(totals.count)];
  const money = formatOutstanding(totals.totalAmount);
  if (money) parts.push(`${money} outstanding`);
  const oldest = overdueOldestPhrase(totals.maxDaysOverdue);
  if (oldest) parts.push(oldest);
  return parts.join(" · ");
}

/** Screen-reader-only text (MUI's `visuallyHidden`, inlined so this file owns
 * no extra import surface). */
const SR_ONLY: SxProps<Theme> = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};

/**
 * `sx` for the CRM nav pill while the alert is live: a relative box (so the
 * wave overlay can sit inside it) and, at `critical`, a whisper of red fill so
 * the tab reads warm even between waves.
 */
export function overdueTabSx(severity: OverdueSeverity): SxProps<Theme> {
  return {
    position: "relative",
    ...(severity === "critical"
      ? {
          // Deliberately faint — the wave carries the urgency; a loud
          // permanently-red tab would fight the active-pill treatment and make
          // the whole nav feel broken.
          backgroundColor: (t: Theme) => alpha(t.palette.error.main, 0.08),
          "&:hover:not(.active)": {
            backgroundColor: (t: Theme) => alpha(t.palette.error.main, 0.14),
          },
        }
      : {}),
  };
}

/**
 * The wave. An absolutely-positioned, clipped overlay inside the pill so the
 * sweep is bounded by the pill's rounded shape — while the DOT, which lives in
 * normal flow, keeps its ripple un-clipped.
 */
export function OverdueWave({
  severity,
}: {
  severity: OverdueSeverity;
}): JSX.Element {
  const cycle = waveCycleMs(severity);
  return (
    <Box
      aria-hidden
      data-testid="crm-nav-overdue-wave"
      sx={{
        position: "absolute",
        inset: 0,
        borderRadius: "inherit",
        overflow: "hidden",
        pointerEvents: "none",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          // A soft band of red light, angled so it reads as a sweep rather
          // than a shutter. Transparent at both ends → it fades in and out
          // instead of snapping at the pill's edges.
          backgroundImage: (t: Theme) =>
            `linear-gradient(100deg, ${alpha(t.palette.error.main, 0)} 30%, ${alpha(
              t.palette.error.main,
              0.34,
            )} 50%, ${alpha(t.palette.error.main, 0)} 70%)`,
          transform: "translateX(-120%)",
          animation: `crmOverdueWave ${cycle}ms cubic-bezier(0.4, 0, 0.2, 1) infinite`,
        },
        // The sweep occupies the first ~14% of the cycle; the rest of the cycle
        // is stillness, parked off-screen. That gap is what keeps a repeating
        // animation from becoming wallpaper.
        "@keyframes crmOverdueWave": {
          "0%": { transform: "translateX(-120%)" },
          "14%": { transform: "translateX(120%)" },
          "100%": { transform: "translateX(120%)" },
        },
        "@media (prefers-reduced-motion: reduce)": {
          display: "none",
        },
      }}
    />
  );
}

/**
 * The dot the wave breaks on: a small solid red disc that emits ONE expanding
 * ring per wave cycle, timed to land just as the sweep arrives.
 */
export function OverdueNavDot({
  severity,
  size = 7,
}: {
  severity: OverdueSeverity;
  size?: number;
}): JSX.Element {
  const cycle = waveCycleMs(severity);
  return (
    <Box
      component="span"
      aria-hidden
      data-testid="crm-nav-overdue-dot"
      data-severity={severity}
      sx={{
        position: "relative",
        flexShrink: 0,
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: "error.main",
        boxShadow: (t: Theme) => `0 0 0 2px ${alpha(t.palette.error.main, 0.18)}`,
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          border: (t: Theme) => `1.5px solid ${t.palette.error.main}`,
          // Starts invisible: the ring only exists in the instant after the
          // wave reaches it.
          opacity: 0,
          animation: `crmOverdueRipple ${cycle}ms cubic-bezier(0.2, 0.7, 0.3, 1) infinite`,
        },
        // 12% ≈ the moment the sweep passes the dot (the sweep runs 0–14%).
        "@keyframes crmOverdueRipple": {
          "0%, 11%": { transform: "scale(1)", opacity: 0 },
          "14%": { transform: "scale(1)", opacity: 0.9 },
          "34%": { transform: "scale(3.2)", opacity: 0 },
          "100%": { transform: "scale(3.2)", opacity: 0 },
        },
        "@media (prefers-reduced-motion: reduce)": {
          "&::after": { animation: "none", opacity: 0 },
        },
      }}
    />
  );
}

export interface OverdueNavAlertProps {
  totals: OverdueTotals;
  severity: OverdueSeverity;
  /** The nav label the dot sits beside ("CRM"). */
  children: React.ReactNode;
}

/**
 * Label + dot + the announcement, wrapped in the hover tooltip. Used by BOTH
 * the desktop nav tab and the mobile drawer row so the two can't drift.
 * The caller supplies the wave (only the pill-shaped tab can clip one).
 */
export function OverdueNavAlert({
  totals,
  severity,
  children,
}: OverdueNavAlertProps): JSX.Element {
  const sentence = overdueNavTooltip(totals);
  return (
    // `describeChild` matters: without it MUI puts the sentence in an
    // `aria-label` on the child, which REPLACES the tab's accessible name —
    // the CRM tab would stop being called "CRM" the moment an invoice fell
    // overdue. As a description it rides alongside the name instead.
    <Tooltip title={sentence} placement="bottom" describeChild>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          position: "relative",
        }}
      >
        {children}
        <OverdueNavDot severity={severity} />
        {/* Announced once when it appears — an operator using a screen reader
            gets the same "you have money to chase" signal as the dot, without
            the tab's visible label turning into a paragraph. */}
        <Box component="span" role="status" sx={SR_ONLY}>
          {sentence}
        </Box>
      </Box>
    </Tooltip>
  );
}
